import { useQuery } from "@tanstack/react-query";
import { Globe } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ChartAreaSkeleton } from "@/components/loading";
import { SearchActivityOverlay } from "@/components/search-activity-overlay";
import { TopCitedDomainsTooltip } from "@/components/tooltips/top-cited-domains-tooltip";
import { Card } from "@/components/ui/card";
import { useDelayedHover } from "@/hooks/use-delayed-hover";
import { useIsMobile } from "@/hooks/use-mobile";
import { getChartColor } from "@/lib/chart-colors";
import { getDisplayPeriodQuery } from "@/lib/display-period";
import { EMPTY_CHART_MESSAGE } from "@/lib/empty-state-copy";
import { shouldShowSearchActivityOverlay, type SearchExecutionActivity } from "@/lib/search-activity";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import type { BrandDashboardPeriod } from "@/state/store/userPreferences";
import type { MostCitedDomainsChartData } from "@/types/data";

interface TopCitedDomainsChartProps {
  brandId: string;
  period: BrandDashboardPeriod;
  activity?: SearchExecutionActivity | null;
}

interface TopCitedDomainSeriesPoint {
  date: string;
  label: string;
  intervalLabel?: string;
  [key: string]: number | string | undefined;
}

const TOP_CITED_DOMAINS_LIMIT = 10;
const TOP_CITED_DOMAINS_CHART_HEIGHT = 240;
const TOP_CITED_DOMAINS_MOBILE_CHART_HEIGHT = 220;
const TOOLTIP_VIEWPORT_EDGE_PADDING = 16;
const getDomainColor = (index: number): string => getChartColor(index);

// eslint-disable-next-line react-refresh/only-export-components
export const formatCitationCountLabel = (count: number): string =>
  `${count.toLocaleString()} citation${count === 1 ? "" : "s"}`;

const formatShare = (share: number): string => {
  const roundedShare = Number.isInteger(share) ? share.toFixed(0) : share.toFixed(1);
  return `${roundedShare}%`;
};

const getSafeHttpUrl = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = new URL(value);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return parsed.toString();
    }
  } catch {
    return null;
  }

  return null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const getDomainFaviconUrl = (domain: string): string | null => {
  const host = domain.trim().replace(/^https?:\/\//, "").split("/")[0];
  if (!host) {
    return null;
  }

  return getSafeHttpUrl(`https://${host}/favicon.ico`);
};

const DomainFavicon = ({ domain }: { domain: string }) => {
  const [hasImageError, setHasImageError] = useState(false);
  const faviconUrl = getDomainFaviconUrl(domain);

  if (!faviconUrl || hasImageError) {
    return (
      <span
        role="img"
        aria-label={`Favicon fallback for ${domain}`}
        className="flex h-5 w-5 items-center justify-center rounded bg-muted/30 text-muted-foreground shrink-0"
      >
        <Globe className="h-3 w-3" aria-hidden="true" />
      </span>
    );
  }

  return (
    <img
      src={faviconUrl}
      alt={`${domain} favicon`}
      className="h-5 w-5 rounded bg-background object-cover shrink-0"
      loading="lazy"
      onError={() => setHasImageError(true)}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const buildTopCitedDomainSeries = (
  chartResponse: MostCitedDomainsChartData | undefined,
): TopCitedDomainSeriesPoint[] => {
  if (!chartResponse) {
    return [];
  }

  const { totalMentions } = chartResponse;
  const orderedKeys = chartResponse.topItems.map((item) => item.key);

  return chartResponse.series.map((point) => {
    const counts = orderedKeys.reduce<Record<string, number>>((acc, key) => {
      const count = point.counts[key] ?? 0;
      acc[key] = totalMentions > 0 ? Math.round((count / totalMentions) * 1000) / 10 : 0;
      return acc;
    }, {});

    return {
      date: point.date,
      label: point.label,
      intervalLabel: point.intervalLabel,
      ...counts,
    };
  });
};

export const TopCitedDomainsChart = ({ brandId, period, activity = null }: TopCitedDomainsChartProps) => {
  const showLoadingOverlay = shouldShowSearchActivityOverlay(activity);
  const trpc = useTRPC();
  const isMobile = useIsMobile();
  const { activeValue: hoveredDomainKey, endHover, startHover } = useDelayedHover<string>(0);
  const chartFrameRef = useRef<HTMLDivElement | null>(null);
  const [reverseTooltipX, setReverseTooltipX] = useState(false);
  const tooltipWidthEstimate = 360;

  const { data: chartResponse, isLoading } = useQuery(
    trpc.charts.getMostCitedDomainsChartData.queryOptions({
      brandId,
      limit: TOP_CITED_DOMAINS_LIMIT,
      ...getDisplayPeriodQuery(period),
    }),
  );

  const topItems = useMemo(() => chartResponse?.topItems ?? [], [chartResponse]);
  const chartData = useMemo(() => buildTopCitedDomainSeries(chartResponse), [chartResponse]);
  const highlightedDomainKey =
    hoveredDomainKey && topItems.some((item) => item.key === hoveredDomainKey) ? hoveredDomainKey : null;

  const hasData = useMemo(
    () =>
      chartData.some((point) =>
        topItems.some((item) => {
          const value = point[item.key];
          return typeof value === "number" && value > 0;
        }),
      ),
    [chartData, topItems],
  );

  const yAxisMax = useMemo(() => {
    if (chartData.length === 0 || topItems.length === 0) {
      return 5;
    }

    const maxValue = chartData.reduce((max, point) => {
      return topItems.reduce((seriesMax, item) => {
        const value = point[item.key];
        if (typeof value === "number") {
          return Math.max(seriesMax, value);
        }
        return seriesMax;
      }, max);
    }, 0);

    return Math.min(100, Math.ceil(maxValue) + 1);
  }, [chartData, topItems]);

  const chartHeight = isMobile ? TOP_CITED_DOMAINS_MOBILE_CHART_HEIGHT : TOP_CITED_DOMAINS_CHART_HEIGHT;

  const updateTooltipDirection = (coordinateX?: number) => {
    if (!chartFrameRef.current || typeof coordinateX !== "number" || typeof window === "undefined") {
      setReverseTooltipX(false);
      return;
    }

    const chartLeft = chartFrameRef.current.getBoundingClientRect().left;
    const cursorViewportX = chartLeft + coordinateX;
    const spaceToLeft = cursorViewportX - TOOLTIP_VIEWPORT_EDGE_PADDING;
    const spaceToRight = window.innerWidth - cursorViewportX - TOOLTIP_VIEWPORT_EDGE_PADDING;

    setReverseTooltipX(spaceToRight < tooltipWidthEstimate && spaceToLeft >= spaceToRight);
  };

  return (
    <Card className="relative flex w-full flex-col gap-0 overflow-visible rounded-xl p-0 shadow-sm lg:h-[312px]">
      <div className="flex min-h-12 shrink-0 flex-col border-b lg:h-12 lg:flex-row">
        <div className="flex min-h-12 flex-1 items-center px-4 sm:px-5 lg:h-full lg:border-r">
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold">Top Cited Domains</span>
            <TopCitedDomainsTooltip />
          </div>
        </div>

        <div className="hidden min-w-[32%] items-center px-4 lg:flex lg:h-full">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground/70">Total</span>
            <span className="text-sm font-semibold tabular-nums">
              {formatCitationCountLabel(chartResponse?.totalMentions ?? 0)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="relative min-w-0 border-b px-3 py-3 sm:px-4 lg:min-h-0 lg:flex-1 lg:border-b-0 lg:border-r lg:px-5">
          {showLoadingOverlay && <SearchActivityOverlay activity={activity} showElapsedTime={false} />}

          {isLoading ? (
            <ChartAreaSkeleton className="border-0 bg-transparent p-0" />
          ) : !hasData ? (
            <div className="flex items-center justify-center text-muted-foreground h-full">
              {!showLoadingOverlay && EMPTY_CHART_MESSAGE}
            </div>
          ) : (
            <div ref={chartFrameRef} className="w-full" style={{ height: chartHeight, minHeight: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  onMouseEnter={(state) => {
                    updateTooltipDirection(state.activeCoordinate?.x);
                  }}
                  onMouseLeave={() => setReverseTooltipX(false)}
                  onMouseMove={(state) => {
                    updateTooltipDirection(state.activeCoordinate?.x);
                  }}
                >
                  <CartesianGrid vertical={false} horizontal={true} strokeDasharray="2 6" stroke="var(--border)" opacity={0.5} />
                  <XAxis
                    dataKey="label"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    className="text-xs"
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    className="text-xs"
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                    width={40}
                    domain={[0, yAxisMax]}
                    tickCount={5}
                    tickFormatter={(value) => formatShare(Number(value))}
                  />
                  {topItems.map((item, index) => (
                    <Line
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      stroke={getDomainColor(index)}
                      strokeWidth={1.75}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity={highlightedDomainKey && highlightedDomainKey !== item.key ? 0.15 : 0.85}
                      dot={false}
                      activeDot={{
                        r: 4,
                        strokeWidth: 2,
                        stroke: "var(--background)",
                        fill: getDomainColor(index),
                      }}
                      connectNulls
                    />
                  ))}
                  <Tooltip
                    allowEscapeViewBox={{ x: true, y: true }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) {
                        return null;
                      }
                      const intervalLabel =
                        (payload[0]?.payload as TopCitedDomainSeriesPoint | undefined)?.intervalLabel ?? label;

                      const sorted = [...topItems].sort((a, b) => {
                        const aVal = (payload.find((e) => e.dataKey === a.key)?.value as number) ?? 0;
                        const bVal = (payload.find((e) => e.dataKey === b.key)?.value as number) ?? 0;
                        return bVal - aVal;
                      });

                      return (
                        <div className="bg-popover/95 backdrop-blur-sm border border-border/60 rounded-xl shadow-xl p-3 min-w-[180px] max-w-[calc(100vw-2rem)] sm:max-w-[26rem]">
                          <div className="text-xs font-medium text-muted-foreground mb-2.5 pb-2 border-b border-border/50">
                            {intervalLabel}
                          </div>
                          <div className="space-y-1.5">
                            {sorted.map((item, index) => {
                              const point = payload.find((entry) => entry.dataKey === item.key);
                              const value = typeof point?.value === "number" ? point.value : 0;

                              return (
                                <div key={item.key} className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div
                                      className="w-1.5 h-1.5 rounded-full shrink-0"
                                      style={{ backgroundColor: getDomainColor(index) }}
                                    />
                                    <span className="text-xs truncate text-foreground/80">{item.display}</span>
                                  </div>
                                  <span className="text-xs font-semibold tabular-nums">{formatShare(value)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }}
                    cursor={{
                      stroke: "var(--border)",
                      strokeWidth: 1,
                    }}
                    reverseDirection={{ x: reverseTooltipX, y: false }}
                    wrapperStyle={{ zIndex: 20 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="relative flex w-full flex-col shrink-0 overflow-hidden lg:min-h-0 lg:w-[32%] lg:overflow-hidden">
          {showLoadingOverlay && <SearchActivityOverlay activity={activity} showElapsedTime={false} />}

          <div className="flex items-center gap-2 border-b px-4 py-2 lg:hidden">
            <span className="text-xs text-muted-foreground/70">Total</span>
            <span className="text-sm font-semibold tabular-nums">
              {formatCitationCountLabel(chartResponse?.totalMentions ?? 0)}
            </span>
          </div>

          {topItems.length === 0 ? (
            <div className="flex items-center justify-center text-xs text-muted-foreground h-full px-4 py-6">
              No ranked domains for this period
            </div>
          ) : (
            <div
              className={cn(
                "w-full",
                isMobile
                  ? "overflow-visible"
                  : "flex-1 min-h-0 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/30",
              )}
            >
              {topItems.map((item, index) => {
                const isMuted = highlightedDomainKey !== null && highlightedDomainKey !== item.key;
                const isHighlighted = highlightedDomainKey === item.key;

                return (
                  // biome-ignore lint/a11y/noStaticElementInteractions: hover handlers for cross-component visual highlighting
                  <div
                    key={item.key}
                    data-muted={isMuted ? "true" : "false"}
                    className={cn(
                      "flex items-start gap-2.5 border-b px-4 py-3 transition-[background-color,opacity] last:border-b-0 sm:items-center",
                      isMuted ? "opacity-30" : "opacity-100",
                      isHighlighted && "bg-muted/15",
                    )}
                    onMouseEnter={() => startHover(item.key)}
                    onMouseLeave={() => endHover(item.key)}
                  >
                    <div
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: getDomainColor(index) }}
                    />
                    <DomainFavicon domain={item.display} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] leading-5 text-foreground/85 break-words sm:truncate">{item.display}</div>
                      <div className="text-[11px] text-muted-foreground/70">{formatCitationCountLabel(item.count)}</div>
                    </div>
                    <div className="shrink-0 pt-0.5 text-[13px] font-semibold tabular-nums text-muted-foreground sm:pt-0">
                      {formatShare(item.share)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
